import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import {
    getServiceConfigoptions,
    mergeServiceConfigoptions,
} from '@/modules/Service/services'
import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMemo, useEffect } from 'react'

export interface MergeConfigoptionsModalProps extends ModalProps {
    serviceID: number
}

interface MergeConfigoptionsForm {
    service_configoption_ids: number[]
    keep_configoption_id: number | null
}

const MergeConfigoptionsModal: React.FC<MergeConfigoptionsModalProps> = ({
    open,
    onClose,
    serviceID,
}) => {
    const { t } = useTranslation()
    const formControl = useForm<MergeConfigoptionsForm>({
        defaultValues: {
            service_configoption_ids: [],
            keep_configoption_id: null,
        },
    })

    const { data: configoptions, isLoading: isLoadingConfigoptions } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.SERVICES_CONFIGOPTIONS, serviceID],
            queryFn: () => getServiceConfigoptions({ serviceID }),
            enabled: open && !!serviceID,
        })

    const { refetch } = useQueryClientCallback({
        reactQueryKeys: [QUERY_KEYS.SERVICES],
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: mergeServiceConfigoptions,
        },
        {
            successMessage: t('merge-configoptions-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
            onSuccess: () => {
                onClose()
                refetch()
            },
        }
    )

    const selectedConfigoptionIds = formControl.watch(
        'service_configoption_ids'
    )

    // Reset keep_configoption_id when selected configoptions change
    useEffect(() => {
        const currentKeepId = formControl.watch('keep_configoption_id')
        if (currentKeepId && !selectedConfigoptionIds.includes(currentKeepId)) {
            formControl.setValue('keep_configoption_id', null)
        }
    }, [selectedConfigoptionIds, formControl])

    const configoptionOptions = useMemo(() => {
        if (!configoptions) return []
        return configoptions.map((config) => ({
            label: `${config.name} - ${config.commitment_value || 0} ${config.unit}`,
            value: config.service_configoption_id,
        }))
    }, [configoptions])

    const keepConfigoptionOptions = useMemo(() => {
        if (!selectedConfigoptionIds || selectedConfigoptionIds.length === 0) {
            return []
        }
        return configoptionOptions.filter((option) =>
            selectedConfigoptionIds.includes(option.value)
        )
    }, [selectedConfigoptionIds, configoptionOptions])

    const onSubmitHandler = async (data: MergeConfigoptionsForm) => {
        if (
            !data.service_configoption_ids ||
            data.service_configoption_ids.length < 2
        ) {
            alert(t('select-at-least-2-configoptions'))
            return
        }

        if (!data.keep_configoption_id) {
            alert(t('select-configoption-to-keep'))
            return
        }

        if (
            !data.service_configoption_ids.includes(data.keep_configoption_id)
        ) {
            alert(t('keep-configoption-must-be-in-selected-list'))
            return
        }

        await mutateAsync({
            serviceID,
            service_configoption_ids: data.service_configoption_ids,
            keep_configoption_id: data.keep_configoption_id,
        })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('merge-service-configoptions')}
            maxWidth="md"
        >
            <div className="text-sm text-gray-600 mb-4 mt-2">
                {t('merge-configoptions-description-v2')}
            </div>

            <GridHookForm
                onSubmit={onSubmitHandler}
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        handler: onClose,
                        disabled: isPending,
                        variant: 'outlined',
                    },
                    {
                        title: t('merge'),
                        type: 'submit',
                        disabled: isPending || isLoadingConfigoptions,
                        loading: isPending,
                        variant: 'contained',
                    },
                ]}
                data={[
                    {
                        formItemProps: {
                            name: 'service_configoption_ids',
                            type: 'select',
                            label: t('select-configoptions-to-merge'),
                            placeholder: t('select-at-least-2-configoptions'),
                            options: configoptionOptions,
                            loading: isLoadingConfigoptions,
                            multiple: true,
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'keep_configoption_id',
                            type: 'select',
                            label: t('select-configoption-to-keep'),
                            placeholder: t('select-which-one-to-keep'),
                            options: keepConfigoptionOptions,
                            disabled:
                                !selectedConfigoptionIds ||
                                selectedConfigoptionIds.length < 2,
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default MergeConfigoptionsModal
